import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense, useCallback, useState} from 'react'
import {ForYouSubSection} from './shared/ForYouSubSection'
import {
  getExpandedSubSections,
  type ForYouSubSectionId,
  type ForYouSubSectionState,
  type ForYouSubSectionVisibility,
} from './shared/pick-expanded-section'
import {ChangesRequested} from './reviews/ChangesRequested'
import {RequiredReviewer} from './mergeability/RequiredReviewer'
import {PendingChecks} from './mergeability/PendingChecks'
import {ApprovableChecks} from './mergeability/ApprovableChecks'
import {AwaitingApprovalChecks} from './mergeability/AwaitingApprovalChecks'
import {MergeConflicts} from './mergeability/MergeConflicts'
import {FailingChecks} from './mergeability/FailingChecks'
import {CompletedChecks} from './mergeability/CompletedChecks'
import {Findings} from './findings/Findings'
import {UnresolvedComments} from './commenting/UnresolvedComments'
import {Deployments} from './deployments/Deployments'
import {ResolvedComments} from './commenting/ResolvedComments'
import {ReviewRequested} from './reviewer/ReviewRequested'
import {ReviewPending} from './reviewer/ReviewPending'
import {ChangesSinceLastReview} from './reviewer/ChangesSinceLastReview'
import {Approved} from './reviews/Approved'
import {CommentReview} from './reviews/CommentReview'
import {ForYouSectionError, ForYouSectionSkeleton} from './ForYouSectionFallbacks'
import {ForYouItemBoundary} from './shared/ForYouItemBoundary'
import {SyncCommentData} from './commenting/shared/SyncCommentData'
import {YourComments} from './commenting/YourComments'
import {IssueComments} from './commenting/IssueComments'

const EMPTY_SUBSECTION_STATE: ForYouSubSectionState = {hasVisibleItems: false, hasLoadingItems: false}

export function ForYouSection() {
  return (
    <ErrorBoundary boundaryName="ForYouSection" fallback={<ForYouSectionError />}>
      <Suspense fallback={<ForYouSectionSkeleton />}>
        <ForYouSectionContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function ForYouSectionContent() {
  // Which subsections currently have confirmed visible items and/or loading
  // placeholders. Fed by each subsection's `onVisibilityChange`.
  const [visibility, setVisibility] = useState<ForYouSubSectionVisibility>({
    needsAttention: EMPTY_SUBSECTION_STATE,
    relevant: EMPTY_SUBSECTION_STATE,
    completed: EMPTY_SUBSECTION_STATE,
  })

  // Sections the user has explicitly toggled; these always win over the
  // priority recommendation.
  const [userOverrides, setUserOverrides] = useState<Partial<Record<ForYouSubSectionId, boolean>>>({})

  const expanded = getExpandedSubSections(visibility, userOverrides)

  const handleToggle = useCallback(
    (id: ForYouSubSectionId, currentlyExpanded: boolean) =>
      setUserOverrides(prev => ({...prev, [id]: !currentlyExpanded})),
    [],
  )

  const handleVisibilityChange = useCallback(
    (id: ForYouSubSectionId, state: ForYouSubSectionState) =>
      setVisibility(prev => updateSubSectionVisibility(prev, id, state)),
    [],
  )

  return (
    <div className="d-flex gap-3 flex-column">
      <SyncCommentData />

      <NeedsAttentionSection
        expanded={expanded.needsAttention}
        onToggle={handleToggle}
        onVisibilityChange={handleVisibilityChange}
      />
      <RelevantToYouSection
        expanded={expanded.relevant}
        onToggle={handleToggle}
        onVisibilityChange={handleVisibilityChange}
      />
      <CompletedSection
        expanded={expanded.completed}
        onToggle={handleToggle}
        onVisibilityChange={handleVisibilityChange}
      />
    </div>
  )
}

function updateSubSectionVisibility(
  visibility: ForYouSubSectionVisibility,
  section: ForYouSubSectionId,
  nextState: ForYouSubSectionState,
) {
  const currentState = visibility[section]
  if (
    currentState.hasVisibleItems === nextState.hasVisibleItems &&
    currentState.hasLoadingItems === nextState.hasLoadingItems
  ) {
    return visibility
  }

  return {...visibility, [section]: nextState}
}

interface SubSectionProps {
  expanded: boolean
  onToggle: (id: ForYouSubSectionId, currentlyExpanded: boolean) => void
  onVisibilityChange: (id: ForYouSubSectionId, state: ForYouSubSectionState) => void
}

function NeedsAttentionSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="needsAttention"
      title="Needs attention"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="ReviewPending">
        <ReviewPending />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="MergeConflicts">
        <MergeConflicts />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ChangesRequested">
        <ChangesRequested />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="FailingChecks">
        <FailingChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ApprovableChecks">
        <ApprovableChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Findings">
        <Findings />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="UnresolvedComments">
        <UnresolvedComments />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ReviewRequested">
        <ReviewRequested />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ChangesSinceLastReview">
        <ChangesSinceLastReview />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}

function RelevantToYouSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="relevant"
      title="Relevant"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="IssueComments">
        <IssueComments />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Approved">
        <Approved />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="CommentReview">
        <CommentReview />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Checks">
        <PendingChecks />
        <AwaitingApprovalChecks />
        <CompletedChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Deployments">
        <Deployments />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="RequiredReviewer">
        <RequiredReviewer />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="YourComments">
        <YourComments />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}

function CompletedSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="completed"
      title="Completed"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="ResolvedComments">
        <ResolvedComments />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}
